import type { WeightedOption } from "../core/types";

export interface RandomSource {
  float(): number;
  range(min: number, max: number): number;
  integer(minInclusive: number, maxInclusive: number): number;
  bool(probability?: number): boolean;
  pick<T>(values: readonly T[]): T;
  weighted<T>(values: readonly WeightedOption<T>[]): T;
  fork(namespace: string): RandomSource;
}

export function createRandomSource(seed: string): RandomSource {
  if (seed.length === 0) throw new Error("A deterministic random source requires a non-empty seed.");

  const seedFactory = xmur3(seed);
  const next = sfc32(seedFactory(), seedFactory(), seedFactory(), seedFactory());

  return {
    float: next,
    range(min: number, max: number): number {
      if (max < min) throw new RangeError("Random range maximum must be at least its minimum.");
      return min + (max - min) * next();
    },
    integer(minInclusive: number, maxInclusive: number): number {
      if (!Number.isInteger(minInclusive) || !Number.isInteger(maxInclusive)) {
        throw new TypeError("Random integer bounds must be integers.");
      }
      if (maxInclusive < minInclusive) {
        throw new RangeError("Random integer maximum must be at least its minimum.");
      }
      return Math.floor(minInclusive + next() * (maxInclusive - minInclusive + 1));
    },
    bool(probability = 0.5): boolean {
      if (probability < 0 || probability > 1) {
        throw new RangeError("Random boolean probability must be between 0 and 1.");
      }
      return next() < probability;
    },
    pick<T>(values: readonly T[]): T {
      if (values.length === 0) throw new RangeError("Cannot pick from an empty collection.");
      const value = values[Math.floor(next() * values.length)];
      if (value === undefined) throw new Error("Deterministic pick produced an invalid index.");
      return value;
    },
    weighted<T>(values: readonly WeightedOption<T>[]): T {
      if (values.length === 0) throw new RangeError("Cannot select from empty weighted options.");
      const totalWeight = values.reduce((total, option) => {
        if (!Number.isFinite(option.weight) || option.weight <= 0) {
          throw new RangeError("Weighted options must use finite weights greater than zero.");
        }
        return total + option.weight;
      }, 0);
      let threshold = next() * totalWeight;
      for (const option of values) {
        threshold -= option.weight;
        if (threshold < 0) return option.value;
      }
      const fallback = values[values.length - 1];
      if (!fallback) throw new Error("Weighted selection could not resolve an option.");
      return fallback.value;
    },
    fork(namespace: string): RandomSource {
      if (namespace.length === 0) throw new Error("A random-source namespace cannot be empty.");
      return createRandomSource(`${seed}:${namespace}`);
    },
  };
}

function xmur3(value: string): () => number {
  let hash = 1779033703 ^ value.length;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 3432918353);
    hash = (hash << 13) | (hash >>> 19);
  }
  return (): number => {
    hash = Math.imul(hash ^ (hash >>> 16), 2246822507);
    hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
    return (hash ^= hash >>> 16) >>> 0;
  };
}

function sfc32(aSeed: number, bSeed: number, cSeed: number, dSeed: number): () => number {
  let a = aSeed;
  let b = bSeed;
  let c = cSeed;
  let d = dSeed;
  return (): number => {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    const result = (a + b + d) | 0;
    d = (d + 1) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = ((c << 21) | (c >>> 11)) + result | 0;
    return (result >>> 0) / 4294967296;
  };
}
